import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { publicAsset } from "../../utils/publicPath";

// Combined scene images + captions keyed by levelId (Group B).
const LEVEL_SCENE = {
  "1": {
    src: publicAsset("/assets/group-b/scene-1/Scene1-GridGuardFacility_v2.png"),
    title: "GridGuard Operations Centre",
    caption: "Your new workplace. This facility monitors and controls the regional power grid around the clock — every substation, every line, every reading flows through here.",
  },
  "4": {
    src: publicAsset("/assets/group-b/scene-2/Scene2-SubstationExterior_v2.png"),
    title: "Cascade Substation",
    caption: "A critical node on the grid. Power passes through here to thousands of homes and businesses. A cyber attack on a site like this can cut electricity to entire neighbourhoods in seconds.",
  },
};

// Placeholder colour layers used for levels without real art yet.
const PLACEHOLDER_LAYERS = [
  { color: "#1e3a5f", speed: 0.2, top: 0,   height: 200, zIndex: 1 },
  { color: "#1e293b", speed: 0.6, top: 150, height: 200, zIndex: 2 },
  { color: "#334155", speed: 1.0, top: 300, height: 200, zIndex: 3 },
];

/**
 * ParallaxScroll — Cinematic intro overlay for a game level.
 *
 * Props:
 *   levelId    (string) — selects layer set + keys the sessionStorage "seen" flag
 *   onComplete (function) — called when animation ends or is skipped
 *   onSkip     (function) — optional override for skip; defaults to onComplete
 *
 * Behaviour:
 *   - Levels with real scene art (LEVEL_SCENE): always play on every visit —
 *     no sessionStorage skip. Players should see the location intro each time
 *     they enter that level.
 *   - Placeholder levels: sessionStorage-gated — plays once per session then
 *     skips on revisit to avoid repeating a low-value animation.
 */
const ParallaxScroll = ({ levelId, onComplete, onSkip }) => {
  const storageKey = "parallax_seen_" + levelId;
  const handleSkip = onSkip || onComplete;
  const hasRealScene = Boolean(LEVEL_SCENE[String(levelId)]);

  useEffect(() => {
    // Only skip automatically for placeholder levels that have been seen.
    if (!hasRealScene && sessionStorage.getItem(storageKey)) {
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId]);

  if (!hasRealScene && sessionStorage.getItem(storageKey)) {
    return null;
  }

  // After the Ken Burns animation completes, hold for 3s so the player has
  // enough time to read the caption before the level loads.
  const handleAnimationComplete = () => {
    setTimeout(() => {
      if (!hasRealScene) sessionStorage.setItem(storageKey, "1");
      onComplete();
    }, 3000);
  };

  const scene = LEVEL_SCENE[String(levelId)];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "#0f172a",
        overflow: "hidden",
      }}
    >
      {/* Skip button */}
      <button
        onClick={() => {
          if (!hasRealScene) sessionStorage.setItem(storageKey, "1");
          handleSkip();
        }}
        className="absolute top-4 right-4 text-sm text-white/70 hover:text-white z-50"
        style={{ position: "absolute", top: 16, right: 16, zIndex: 51 }}
      >
        Skip →
      </button>

      {scene ? (
        <>
          {/* Scene image — full image visible from first frame.
              Ken Burns scale-in (1.06 → 1.0) over 3s gives cinematic motion
              without cropping. 2s dwell after settle before onComplete fires. */}
          <motion.div
            initial={{ scale: 1.06 }}
            animate={{ scale: 1 }}
            transition={{ duration: 3, ease: "easeOut" }}
            onAnimationComplete={handleAnimationComplete}
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${scene.src})`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />

          {/* Caption overlay — fades in after 0.8s so the image settles first */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 52,
              padding: "32px 40px",
              background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)",
            }}
          >
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#22d3ee", marginBottom: 6 }}>
              Location
            </p>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#f1f5f9", marginBottom: 8 }}>
              {scene.title}
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: "#94a3b8", maxWidth: 560, lineHeight: 1.6 }}>
              {scene.caption}
            </p>
          </motion.div>
        </>
      ) : (
        /* Placeholder coloured blocks for levels without real art */
        PLACEHOLDER_LAYERS.map((layer, i) => {
          const isLast = i === PLACEHOLDER_LAYERS.length - 1;
          return (
            <motion.div
              key={i}
              initial={{ x: -(layer.speed * 800) }}
              animate={{ x: 0 }}
              transition={{ duration: 1.5 + layer.speed, ease: "easeOut" }}
              onAnimationComplete={isLast ? handleAnimationComplete : undefined}
              style={{
                position: "absolute",
                top: layer.top,
                left: 0,
                width: "100%",
                height: layer.height,
                background: layer.color,
                zIndex: layer.zIndex,
              }}
            />
          );
        })
      )}
    </div>
  );
};

export default ParallaxScroll;
